import { RefObject, useCallback, useEffect, useState } from 'react'
import { ChatSelectionTabTypes } from './chat-list'
import { addRoomsToUserPermissionsCache } from '$logic/user-permissions'
import { useScrollDirection } from '$hooks/use-scroll-direction'
import { createCallbackManager } from '$func/callbacks'

const INITIAL_RENDER_LIMIT = 20
export const RENDER_LIMIT_INCREASE_AMOUNT = 10
export const INCREASE_LIMIT_OFFSET = 5

export function useItemsRenderLimit({
  totalCount = 0,
  calculatePermissionsOnMount = true,
  calculatePermissionsOnScroll = true,
}: {
  totalCount: number
  calculatePermissionsOnMount?: boolean
  calculatePermissionsOnScroll?: boolean
}) {
  const [limit, setLimit] = useState(INITIAL_RENDER_LIMIT)

  useEffect(() => {
    if (calculatePermissionsOnMount)
      addRoomsToUserPermissionsCache(
        INITIAL_RENDER_LIMIT + RENDER_LIMIT_INCREASE_AMOUNT,
      )
  }, [])

  const increaseItemsRenderLimit = () => {
    if (calculatePermissionsOnScroll)
      addRoomsToUserPermissionsCache(RENDER_LIMIT_INCREASE_AMOUNT)

    setLimit(oldLimit =>
      Math.min(oldLimit + RENDER_LIMIT_INCREASE_AMOUNT, totalCount),
    )
  }

  return {
    itemsRenderLimit: limit,
    increaseItemsRenderLimit,
    increaseNeeded: limit < totalCount,
  }
}

type VisitedFoldersMap = Record<ChatSelectionTabTypes, boolean>
const visitedFoldersMapInitialState: VisitedFoldersMap = {
  ALL: true,
  PV: false,
  CHANNEL: false,
  GROUP: false,
  WAPP_ROOM: false,
  PRIVATE_SPACE: false,
  WORKSPACES: false,
}

export function useVisitedFolders() {
  const [visitedFoldersMap, setVisitedFoldersMap] = useState<VisitedFoldersMap>(
    visitedFoldersMapInitialState,
  )

  const visitFolder = useCallback(
    (folder: ChatSelectionTabTypes) =>
      setVisitedFoldersMap(map => ({ ...map, [folder]: true })),
    [],
  )

  return {
    visitedFoldersMap,
    visitFolder,
  }
}

const chatListScrollDirectionCallBacks = createCallbackManager()
let isChatListGoingDown = false
export function useChatListScrollDirectionProducer(
  chatListWrapperRef: RefObject<HTMLElement>,
) {
  const { isGoingDown } = useScrollDirection(chatListWrapperRef)

  useEffect(() => {
    isChatListGoingDown = isGoingDown
    chatListScrollDirectionCallBacks.runCallbacks(isGoingDown)
  }, [isGoingDown])
}

export function useIsChatListGoingDown() {
  const [isGoingDown, setIsGoingDown] = useState(isChatListGoingDown)

  useEffect(() => {
    chatListScrollDirectionCallBacks.addCallback(setIsGoingDown)

    return () => {
      chatListScrollDirectionCallBacks.removeCallback(setIsGoingDown)
    }
  }, [])

  return isGoingDown
}
